// Formats shared by the CRM Postventa gestión screens (Rollo, Gestiones IPC): Argentine formats, Spanish labels
// for the server codes and words or a dash for missing data, never an empty cell.

export const EMPTY = "—";

// The four priorities of the design (Rollo only assigns ALTA and MEDIA; IPC uses the four).
export const PRIORIDAD_LABELS = {
  CRITICA: "Crítica",
  ALTA: "Alta",
  MEDIA: "Media",
  BAJA: "Baja",
};

// PriorityChip tone and number of lit bars.
export const PRIORIDAD_LEVELS = {
  CRITICA: { tone: "critical", bars: 4 },
  ALTA: { tone: "high", bars: 3 },
  MEDIA: { tone: "medium", bars: 2 },
  BAJA: { tone: "low", bars: 1 },
};

const normalizeCode = (code) => String(code ?? "").trim().toUpperCase();

export const labelFor = (labels, code) => {
  const raw = String(code ?? "").trim();
  if (!raw) return EMPTY;
  return labels[normalizeCode(raw)] || raw;
};

export const codeOf = (code) => normalizeCode(code);

const pad = (value) => String(value).padStart(2, "0");
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

// Server dates come without time zone ("2026-10-12T09:30:00"): read the calendar day from the text so the
// browser time zone never shifts it.
const dateParts = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return { year: String(value.getFullYear()), month: pad(value.getMonth() + 1), day: pad(value.getDate()) };
  }

  const match = ISO_DATE.exec(String(value));
  if (match) {
    return { year: match[1], month: match[2], day: match[3] };
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : dateParts(parsed);
};

export const formatDate = (value) => {
  const parts = dateParts(value);
  return parts ? `${parts.day}/${parts.month}/${parts.year}` : EMPTY;
};

export const formatShortDate = (value) => {
  const parts = dateParts(value);
  return parts ? `${parts.day}/${parts.month}` : EMPTY;
};

// "YYYY-MM-DD" of a value, for comparisons and date inputs.
export const toIsoDate = (value) => {
  const parts = dateParts(value);
  return parts ? `${parts.year}-${parts.month}-${parts.day}` : null;
};

export const todayIso = (now = new Date()) => toIsoDate(now);

// "HH:MM" of a server date-time, read from the text like the calendar day; null when it has no time.
const ISO_TIME = /T(\d{2}):(\d{2})/;
export const formatTime = (value) => {
  const match = ISO_TIME.exec(String(value ?? ""));
  return match ? `${match[1]}:${match[2]}` : null;
};

export const formatCuit = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return EMPTY;

  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
  }

  return raw;
};

const wholeDays = (days) => Math.max(0, Math.round(Number(days) || 0));

export const formatRelativeDays = (days) => {
  const n = wholeDays(days);
  if (n === 0) return "hoy";
  return n === 1 ? "hace 1 día" : `hace ${n} días`;
};

export const formatDuration = (days) => {
  const n = wholeDays(days);
  if (n === 0) return "Mismo día";
  return n === 1 ? "1 día" : `${n} días`;
};

export const formatGestiones = (total) => {
  const n = Number(total) || 0;
  return `${n} ${n === 1 ? "gestión" : "gestiones"}`;
};

// Demora threshold as the server sends it (list diasParaDemora): a positive whole number, or null when the server
// does not send it. The client never assumes a number of days.
export const demoraDays = (value) => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};
